import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, map } from 'rxjs/operators';
import { FieldMapContext } from '../field-map-context.model';
import { CropService } from '../../shared/crop/service/crop.service';
import { StudyService } from '../../shared/study/service/study.service';
import { ProgramService } from '../../shared/program/service/program.service';
import { Program } from '../../shared/program/model/program';
import { StudySearchResponse } from '../../shared/study/model/study-search-response.model';
import { ParamContext } from '../../shared/service/param.context';
import { HttpContext } from '@angular/common/http';
import { SkipErrorInterceptor } from '../../blocks/interceptor/error.interceptor';

export interface FilterEventData {
    cropName: string;
    programUUID?: string;
    trialId?: number;
    instanceId?: number;
}

@Component({
    selector: 'jhi-study-instances-filter-select',
    templateUrl: './study-instances-filter-select.component.html',
    styleUrls: ['./study-instances-filter-select.component.scss']
})
export class StudyInstancesFilterSelectComponent implements OnInit {

    @Output()
        filterChanged = new EventEmitter<FilterEventData>();

    crops: string[];
    selectedCropName: string;

    /********PROGRAMS************/

    programsById: { [key: string]: Program } = {};
    selectedProgramUUID: string;
    programChanged = new Subject<string>();
    programDropdownOptions: any;

    totalItemCount = 0;
    pageSize = 20;
    pageIterator = 1;

    isLoading = false;
    helpLink: string;

    programItems: any[];

    searchSubject = new Subject<string>();

    /***** Studies ********/
    studies: StudySearchResponse[] = [];
    selectedStudy: StudySearchResponse;

    constructor(private cropService: CropService,
                private studyService: StudyService,
                private programService: ProgramService,
                private fieldmapContext: FieldMapContext,
                private paramContext: ParamContext) {
    }

    async ngOnInit(): Promise<void> {
        this.crops = await this.cropService.getCrops().toPromise();
        this.selectedCropName = this.crops[0];

        const response = await this.getPrograms(1, undefined).toPromise();
        this.programItems = response.body;

        this.searchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe((searchTerm) => {
            this.searchPrograms(searchTerm);
        }
        );

        this.filterChanged.emit({
            cropName: this.selectedCropName
        });
    }

    /*********************PROGRAMS*************************/
    onProgramSearch(searchTerm: string) {
        this.searchSubject.next(searchTerm);
    }

    searchPrograms(searchTerm: string): void {
        this.pageIterator = 1;
        this.programItems = [];
        this.getPrograms(this.pageIterator, searchTerm).subscribe((res) => {
            const programs = res.body;
            this.programItems = this.programItems.concat(programs.map(this.mapProgram));
        }, (err) => {
        });
    }

    clearPrograms(): void {
        this.pageIterator = 1;
        this.selectedProgramUUID = null;
        this.filterChanged.emit({
            cropName: this.selectedCropName
        });
    }

    fetchMorePrograms(searchTerm: string): void {
        if (this.canLoadMore()) {
            this.pageIterator = this.pageIterator + 1;
            this.getPrograms(this.pageIterator, searchTerm).subscribe((res) => {
                const programs = res.body;
                this.programItems = this.programItems.concat(programs.map(this.mapProgram));
            }, (err) => {
            });
        }
    }

    private canLoadMore(): boolean {
        return (this.pageIterator * this.pageSize) < this.totalItemCount;
    }

    private getPrograms(page: number, query = '') {
        this.isLoading = true;
        return this.programService.getPrograms(this.selectedCropName, query || '', {
            page: page - 1,
            size: this.pageSize
        }, { context: new HttpContext().set(SkipErrorInterceptor, true) }).pipe(map((res) => {
            this.totalItemCount = Number(res.headers.get('X-Total-Count'));
            Object.assign(this.programsById, res.body.reduce((prev, curr) => {
                prev[curr.uniqueID] = curr;
                return prev;
            }, {}));
            return res;
        })).pipe(
            finalize(() => this.isLoading = false)
        );
    }

    private mapProgram(program): any {
        return {
            id: program.uniqueID,
            label: program.name
        };
    }

    async onProgramChange($event: any) {

        if (this.selectedProgramUUID) {
            this.fieldmapContext.programUUID = this.selectedProgramUUID;
            this.paramContext.cropName = this.selectedCropName;
            this.paramContext.programUUID = this.selectedProgramUUID;
            const searchStudiesResponse = await this.studyService.searchStudies( {}, undefined).toPromise();
            this.studies = searchStudiesResponse.body;

            this.filterChanged.emit({
                cropName: this.selectedCropName,
                programUUID: this.selectedProgramUUID,
                trialId: this.selectedStudy ? this.selectedStudy.studyId : null,
                instanceId: null
            });
        } else {
            this.studies = [];
            this.selectedStudy = null;
        }

    }

    onCropChange() {
        this.fieldmapContext.cropName = this.selectedCropName;
        this.filterChanged.emit({
            cropName: this.selectedCropName,
            programUUID: this.selectedProgramUUID,
            trialId: this.selectedStudy ? this.selectedStudy.studyId : null,
            instanceId: null
        });

        this.programItems = [];
        this.selectedProgramUUID = null;
        this.studies = [];
        this.selectedStudy = null;
    }

    onStudyChange() {
        this.filterChanged.emit({
            cropName: this.selectedCropName,
            programUUID: this.selectedProgramUUID,
            trialId: this.selectedStudy ? this.selectedStudy.studyId : null,
            instanceId: null
        });
    }

    clearFilter() {
        this.programItems = [];
        this.selectedProgramUUID = null;
        this.studies = [];
        this.selectedStudy = null;
        this.onCropChange();
    }

}
